/**
 * TheaterDetailsPanel — surfaces venue + per-screen details inline in
 * the DCP Distribution expand panel so the operator doesn't have to
 * navigate to the Venue page to confirm "is this the Dolby room with
 * the old GDC server?" or "does the cert expire next week?".
 *
 * Ship 1 of the v10.21.x DCP UX refactor (operator feedback
 * 2026-06-13: "I want to see much better what theater and all the
 * details on the distribution page.").
 *
 * Renders, in order:
 *   1. Certificate-expiry banner if ANY screen's certificate_not_after
 *      is within 30 days or already past.
 *   2. Tech contact card (name, email, phone).
 *   3. Per-screen rows showing projector make/model, server make/model,
 *      certificate thumbprint (last 8 chars), cert validity status,
 *      ingest method + notes.
 *
 * Lazy-fetches /api/venues/{id} + /api/venues/{id}/screens on mount.
 * The component is only mounted inside the expand panel, so the
 * fetch fires once the operator actually opens the card.
 */
import { useEffect, useState } from 'react';
import { Box, Typography, Chip, Tooltip, Link as MuiLink, CircularProgress } from '@mui/material';
import {
  Warning as WarningIcon,
  PersonOutline as ContactIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const SECTION_LABEL_SX = {
  minWidth: 80,
  color: 'text.secondary',
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  mt: 0.25,
};

const ROW_SX = { display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 };

function fmtCertExpiry(notAfter) {
  if (!notAfter) return null;
  const d = new Date(notAfter);
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000);
  if (days < 0) return { label: `Cert expired ${-days}d ago`, color: 'error.main' };
  if (days <= 30) return { label: `Cert expires in ${days}d`, color: 'warning.main' };
  return { label: `Cert valid through ${d.toLocaleDateString()}`, color: 'text.secondary' };
}

function shortThumbprint(thumb) {
  if (!thumb) return null;
  const clean = thumb.replace(/[:\s]/g, '').toUpperCase();
  if (clean.length <= 8) return clean;
  return `…${clean.slice(-8)}`;
}

function ScreenRow({ screen }) {
  const cert = fmtCertExpiry(screen.certificate_not_after);
  const thumb = shortThumbprint(screen.certificate_thumbprint);
  const projector = [screen.projector_make, screen.projector_model].filter(Boolean).join(' ') || null;
  const server = [screen.server_make, screen.server_model].filter(Boolean).join(' ') || null;
  return (
    <Box sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{screen.name || '(unnamed screen)'}</Typography>
        {screen.screen_type && screen.screen_type !== 'Standard' && (
          <Chip size="small" label={screen.screen_type} sx={{ height: 18, fontSize: '0.62rem' }} />
        )}
        {screen.max_resolution && (
          <Chip size="small" label={screen.max_resolution} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
        )}
        {screen.is_3d && <Chip size="small" label="3D" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
        {screen.is_laser && <Chip size="small" label="Laser" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />}
        {screen.ingest_method && (
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
            · ingest via {screen.ingest_method}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25, fontSize: '0.7rem' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>Projector</Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{projector || <em style={{ opacity: 0.5 }}>not on file</em>}</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>Server</Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{server || <em style={{ opacity: 0.5 }}>not on file</em>}</Typography>
        {(thumb || cert) && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>Certificate</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              {thumb && (
                <Tooltip title={`Full thumbprint: ${screen.certificate_thumbprint}`}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>{thumb}</Typography>
                </Tooltip>
              )}
              {cert && (
                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: cert.color, fontWeight: cert.color === 'error.main' ? 700 : 400 }}>
                  {cert.label}
                </Typography>
              )}
            </Box>
          </>
        )}
        {screen.ingest_notes && (
          <>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>Ingest notes</Typography>
            <Typography variant="caption" sx={{ fontSize: '0.68rem', fontStyle: 'italic' }}>{screen.ingest_notes}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function TheaterDetailsPanel({ venueId }) {
  const [venue, setVenue] = useState(null);
  const [screens, setScreens] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!venueId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      api.get(`/api/venues/${venueId}`).then((r) => r.data),
      api.get(`/api/venues/${venueId}/screens`).then((r) => r.data || []),
    ])
      .then(([v, s]) => {
        if (cancelled) return;
        setVenue(v);
        setScreens(s);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Could not load theater details');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [venueId]);

  if (!venueId) return null;
  if (loading) {
    return (
      <Box sx={ROW_SX}>
        <Typography variant="caption" sx={SECTION_LABEL_SX}>Theater</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <CircularProgress size={12} />
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>loading…</Typography>
        </Box>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={ROW_SX}>
        <Typography variant="caption" sx={SECTION_LABEL_SX}>Theater</Typography>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'error.main' }}>{error}</Typography>
      </Box>
    );
  }

  // Cert banner: red if ANY screen's cert is past or within 30 days.
  const certIssues = (screens || []).filter((s) => {
    if (!s.certificate_not_after) return false;
    const days = (new Date(s.certificate_not_after).getTime() - Date.now()) / 86_400_000;
    return days <= 30;
  });
  const anyExpired = certIssues.some((s) => new Date(s.certificate_not_after) < new Date());

  return (
    <Box sx={{ mb: 1.5 }}>
      {certIssues.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            mb: 1,
            border: '1px solid',
            borderColor: anyExpired ? 'error.main' : 'warning.main',
            borderRadius: 1,
            bgcolor: anyExpired ? 'rgba(244,67,54,0.06)' : 'rgba(255,167,38,0.06)',
          }}
        >
          <WarningIcon sx={{ fontSize: 16, color: anyExpired ? 'error.main' : 'warning.main' }} />
          <Typography variant="caption" sx={{ fontSize: '0.74rem', fontWeight: 600, color: anyExpired ? 'error.main' : 'warning.main' }}>
            {anyExpired
              ? `Certificate problem: ${certIssues.length} screen${certIssues.length === 1 ? '' : 's'} need${certIssues.length === 1 ? 's' : ''} a new cert before next KDM issue.`
              : `Heads-up: ${certIssues.length} screen${certIssues.length === 1 ? '' : 's'} have certs expiring within 30 days.`}
          </Typography>
        </Box>
      )}

      <Box sx={ROW_SX}>
        <Typography variant="caption" sx={SECTION_LABEL_SX}>Theater</Typography>
        <Box sx={{ flex: 1 }}>
          {/* Tech contact */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <ContactIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ fontSize: '0.74rem' }}>
                {venue?.tech_contact_name || <em style={{ opacity: 0.5 }}>no tech contact on file</em>}
              </Typography>
            </Box>
            {venue?.tech_contact_email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <MuiLink
                  href={`mailto:${venue.tech_contact_email}`}
                  sx={{ fontSize: '0.74rem' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {venue.tech_contact_email}
                </MuiLink>
              </Box>
            )}
            {venue?.tech_contact_phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: '0.74rem' }}>{venue.tech_contact_phone}</Typography>
              </Box>
            )}
          </Box>

          {/* Per-screen rows */}
          {(screens || []).length === 0 ? (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
              No screens on file for this venue.
            </Typography>
          ) : (
            screens.map((s) => <ScreenRow key={s.id} screen={s} />)
          )}
        </Box>
      </Box>
    </Box>
  );
}
